import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import {
  Megaphone, School, BookOpen, Award, Calendar, Star, Bell,
  Rocket, Globe, Zap, Users, AlertCircle, CheckCircle, Info,
  Heart, Trophy, GraduationCap, Clipboard, FileText, Building, X,
} from "lucide-react";

const ICON_MAP = {
  Megaphone, School, BookOpen, Award, Calendar, Star, Bell,
  Rocket, Globe, Zap, Users, AlertCircle, CheckCircle, Info,
  Heart, Trophy, GraduationCap, Clipboard, FileText, Building,
};

const CACHE_KEY = "ticker_feed_v2";
const CACHE_TTL = 2 * 60 * 1000;

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    return Date.now() - ts < CACHE_TTL ? data : null;
  } catch { return null; }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

function useWindowWidth() {
  const [w, setW] = useState(() => window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

export default function NewsTicker() {
  const [feed, setFeed] = useState(() => readCache());
  const [closed, setClosed] = useState(false);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);
  const animRef = useRef(null);
  const posRef = useRef(0);
  const windowWidth = useWindowWidth();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ticker/feed");
      writeCache(data);
      setFeed(data);
    } catch (e) {
      console.warn("Ticker feed error:", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const settings = feed?.settings || {};
  const items = feed?.items || [];
  const hasItems = items.length > 0;

  // Scroll animation — declared unconditionally
  useEffect(() => {
    if (!hasItems) return;
    const el = trackRef.current;
    if (!el) return;
    posRef.current = 0;
    const speed = Math.max(5, Number(settings.scroll_speed || 40));
    let last = null;
    function step(ts) {
      if (!last) last = ts;
      const dt = Math.min(ts - last, 100);
      last = ts;
      if (!paused) {
        posRef.current -= (speed * dt) / 1000;
        const half = el.scrollWidth / 2;
        if (Math.abs(posRef.current) >= half) posRef.current = 0;
        el.style.transform = `translateX(${posRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(step);
    }
    animRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animRef.current);
  }, [hasItems, paused, settings.scroll_speed]); // eslint-disable-line react-hooks/exhaustive-deps

  // All conditionals after hooks
  if (!feed) return null;
  if (!settings.enabled) return null;
  if (closed) return null;

  // Device visibility
  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;
  if (isMobile && settings.mobile_visible === false) return null;
  if (isTablet && settings.tablet_visible === false) return null;
  if (isDesktop && settings.desktop_visible === false) return null;

  // Build styles
  let bg = settings.bg_color || "#0F172A";
  if (settings.bg_gradient) bg = settings.bg_gradient;

  const wrapStyle = {
    height: `${settings.height || 40}px`,
    background: settings.glassmorphism
      ? `rgba(255,255,255,${settings.glass_opacity ?? 0.15})`
      : bg,
    backdropFilter: settings.glassmorphism ? `blur(${settings.glass_blur || 8}px)` : undefined,
    WebkitBackdropFilter: settings.glassmorphism ? `blur(${settings.glass_blur || 8}px)` : undefined,
    fontSize: `${settings.font_size || 14}px`,
    fontFamily: settings.font_family || undefined,
    fontWeight: settings.font_weight || 500,
    fontStyle: settings.font_style || "normal",
    color: settings.font_color || "#FFFFFF",
    letterSpacing: settings.letter_spacing ? `${settings.letter_spacing}px` : undefined,
    borderRadius: settings.rounded ? `${settings.border_radius || 8}px` : undefined,
    boxShadow: settings.shadow || undefined,
    border: settings.border_color ? `1px solid ${settings.border_color}` : settings.border || undefined,
    position: settings.sticky ? "sticky" : "relative",
    top: settings.sticky ? 0 : undefined,
    zIndex: 9999,
    opacity: settings.ticker_opacity ?? 1,
    overflow: "hidden",
    direction: settings.direction === "rtl" ? "rtl" : "ltr",
    userSelect: "none",
    display: "flex",
    alignItems: "center",
    width: "100%",
  };

  const gap = Number(settings.gap || 60);
  const paddingX = Number(settings.padding_x || 0);
  const sep = settings.separator !== undefined ? settings.separator : "•";
  const closeBtn = settings.show_close_btn ? (
    <button onClick={() => setClosed(true)} aria-label="Close"
      style={{ padding: "0 12px", opacity: 0.7, cursor: "pointer", background: "none", border: "none", color: "inherit", flexShrink: 0 }}>
      <X style={{ width: 14, height: 14 }} />
    </button>
  ) : null;

  // Empty state
  if (!hasItems) {
    return (
      <div style={wrapStyle}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.6, gap: 8 }}>
          <Bell style={{ width: 14, height: 14, flexShrink: 0 }} />
          <span>No announcements available.</span>
        </div>
        {closeBtn}
      </div>
    );
  }

  const doubled = settings.auto_repeat !== false ? [...items, ...items] : items;

  return (
    <div style={wrapStyle}>
      <div style={{ flex: 1, overflow: "hidden", position: "relative", height: "100%", display: "flex", alignItems: "center" }}>
        <div
          ref={trackRef}
          style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap", willChange: "transform" }}
          onMouseEnter={() => settings.pause_on_hover !== false && setPaused(true)}
          onMouseLeave={() => settings.pause_on_hover !== false && setPaused(false)}
          onTouchStart={() => settings.pause_on_hover !== false && setPaused(true)}
          onTouchEnd={() => settings.pause_on_hover !== false && setPaused(false)}
        >
          {doubled.map((item, i) => {
            const Ico = ICON_MAP[item.icon];
            const inner = (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", paddingRight: gap, paddingLeft: paddingX }}>
                {sep && i > 0 && <span style={{ marginRight: 12, opacity: 0.5 }}>{sep}</span>}
                {Ico && <Ico style={{ width: 14, height: 14, color: item.color || "currentColor", flexShrink: 0 }} />}
                <span style={{ marginLeft: Ico ? 8 : 0 }}>{item.headline}</span>
                {item.description && (
                  <span style={{ marginLeft: 8, opacity: 0.65, fontSize: "0.85em" }}>{item.description}</span>
                )}
              </span>
            );
            return item.link ? (
              <a key={i} href={item.link}
                target={item.link_new_tab ? "_blank" : "_self"}
                rel="noopener noreferrer"
                style={{ color: "inherit", textDecoration: "none" }}>
                {inner}
              </a>
            ) : inner;
          })}
        </div>
      </div>
      {closeBtn}
    </div>
  );
}
